import React, { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { Organization } from 'shared/types/ObjectTypes';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import NoResults from 'shared/components/ui/NoResults';
import Pagination from 'shared/components/ui/Pagination';
import { page_size } from 'shared/api/apiUtils';
import {
  listPageClasses,
  tableClasses,
  buttonClasses,
  paginationClasses,
  skeletonClasses,
} from 'styles/tailwindClassnames';

type OrganizationsListProps = {
  organizations: Array<Organization>;
  onSelectOrg: Function;
  onAddOrg: MouseEventHandler;
  superuser: boolean;
  onPageClicked: Function;
  totalCount: number;
  pageLoaded: boolean;
  take: number;
  offset: number;
  searchString: string;
  onSearch: (term: string) => void;
};

const OrganizationsList = ({
  organizations,
  onSelectOrg,
  onAddOrg,
  superuser,
  onPageClicked,
  totalCount,
  pageLoaded,
  take,
  offset,
  searchString,
  onSearch,
}: OrganizationsListProps) => {
  const { t } = useTranslation();

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1
            data-testid="organizations_page_header"
            className={listPageClasses.title}
          >
            {t('Workspaces')}
          </h1>
          {superuser && (
            <button className={buttonClasses.primary} onClick={onAddOrg}>
              {t('Create Workspace')}
            </button>
          )}
        </div>
      </div>
      <div className={listPageClasses.body}>
        <div className="mx-auto flex w-full max-w-list-table flex-col">
          <div className="mb-3 flex flex-row items-end gap-3">
            <GlobalSearch
              onChangeGlobalSearch={onSearch}
              savedString={searchString}
              placeholder={t('Search by name')}
            />
          </div>
          <table className={tableClasses.container + ' table-fixed'}>
            <thead className={tableClasses.header}>
              <tr>
                <th className={tableClasses.headerCell + ' w-1/3'}>
                  {t('Name')}
                </th>
                <th className={tableClasses.headerCell}>{t('Description')}</th>
              </tr>
            </thead>
            {!pageLoaded ? (
              <tbody className="text-body-md">
                {[...Array(8)].map((_, i) => (
                  <tr
                    key={i}
                    className="border-b border-teal-t30 bg-white last:border-b-0"
                    data-testid="loading"
                  >
                    <td className="w-1/3 p-3">
                      <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                    </td>
                    <td className="p-3">
                      <div className={`h-4 w-64 ${skeletonClasses.base}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody className="text-body-md transition-colors duration-300">
                {organizations.map((org: Organization) => {
                  return (
                    <tr
                      key={org.id}
                      className="border-b border-teal-t30 bg-white transition-colors duration-300 last:border-b-0 hover:bg-greyscale-11"
                      onClick={(e) =>
                        e.metaKey || e.ctrlKey
                          ? window.open(`/workspaces/${org.id}`, '_blank')
                          : onSelectOrg(org.id)
                      }
                    >
                      <td className="w-1/3 cursor-pointer p-3 transition-colors duration-300">
                        {org.name}
                      </td>
                      <td className="p-3 transition-colors duration-300">
                        <p className="break-words">{org.description}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
            {pageLoaded && organizations.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={2} className="py-16">
                    <NoResults
                      headerMsg={t('No Results Found')}
                      labelMsg={t(
                        'You may need to adjust your search or filters to find what you are looking for',
                      )}
                    />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          {totalCount > page_size && (
            <Pagination
              showText={true}
              handlePageClick={onPageClicked}
              pageCount={Math.ceil(totalCount / take)}
              resultStart={offset + 1}
              resultEnd={
                offset + take < totalCount ? offset + take : totalCount
              }
              totalCount={totalCount}
              take={take}
              loading={!pageLoaded}
              mainclassname={paginationClasses.container}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationsList;
